import React from "react"
import { Button, Modal, Table, Spinner, Col, Form, Row, OverlayTrigger, Popover } from "react-bootstrap"
import { toast } from "react-toastify"
import { connect } from "react-redux"
import { concat, of, Subject, Subscription, forkJoin } from "rxjs"
import { catchError, map, switchMap } from "rxjs/operators"
import ActionButton from "../ActionButton"
import { AppPageWrapper } from "../NavMenu/AppPageWrapper"
import { RouteComponentProps } from "react-router"
import { Home } from "../../config/Routes"
import Checkbox from "../Checkbox"
import { APIError } from "../../api/ObservableFromFetch"
import { AppState, WorkforceClinicPortal } from "../../AppState"
import { isPortalAdmin, isSiteAdmin } from "../../authentication/isAdminRole"
import { GetSelectedPortal, getUserNameForSelectedCompany } from "../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"
import { Typeahead } from "react-bootstrap-typeahead"
import { FaAngleDown, FaInfoCircle } from "react-icons/fa"
import ServicesGet, { AccountServicesGet, AccountServicesSave, SaveServices } from "../../api/Administration/OrderableServices"
import AutoFilterCompany from "../../api/Companies/CompanyModel/AutoFilterCompany"
import Service from "../../api/Administration/Model/Service"
import AccountService from "../../api/Administration/Model/AccountService"
import CompanyInput from "../FormattedInputs/CompanyInput"
import ImplementationsGetObservable from "../../api/Implementations/ImplementationsGet"
import ImplementationData from "../../api/Implementations/Model/ImplementationData"
import FormatError from "../../formatters/FormatError"
import { DisabledDefaultOrderableService } from "../../config/Constants"

interface OrderableServicesModalAuthProps {
  isPortalAdmin?: boolean
  isSiteAdmin?: boolean
  UserName?: string
  Portal?: WorkforceClinicPortal
}

interface OrderableServicesModalProps { }

interface OrderableServicesModalState {
  saving: boolean
  loading: boolean
  loadingServices: boolean
  services: Service[]
  accountServices: AccountService[]
  portalServices: AccountService[]
  data: ServiceData[]
  portals: ImplementationData[]
  companies: AutoFilterCompany[]
  selectedPortal?: ImplementationData
  selectedCompany?: AutoFilterCompany
  selectedCompanyID?: number
}

interface ServiceData {
  AccountServiceID?: number
  ImplementationID?: number
  AccountID?: number
  ServiceID: number
  ServiceName: string
  Active: boolean
  ActivateAll: boolean
  IsDefault: boolean
  Disabled?: boolean
}

export class OrderableServicesModal extends React.PureComponent<OrderableServicesModalProps & OrderableServicesModalAuthProps, OrderableServicesModalState> {
  emptyState: OrderableServicesModalState = {
    loading: false,
    loadingServices: false,
    saving: false,
    services: [],
    accountServices: [],
    portalServices: [],
    data: [],
    portals: [],
    companies: [],
    selectedPortal: undefined,
    selectedCompany: undefined,
    selectedCompanyID: undefined
  }
  state: OrderableServicesModalState = { ...this.emptyState }

  portalTypeahead = React.createRef<Typeahead<ImplementationData>>()
  companyTypeahead = React.createRef<Typeahead<AutoFilterCompany>>()
  loadPortalsSubscription?: Subscription
  loadSubscription?: Subscription
  loadCompanyServicesSubscription?: Subscription
  saveSubscription?: Subscription
  implementationName$ = new Subject<string>()
  company$ = new Subject<AutoFilterCompany>()

  componentWillMount() {
    if (this.props.isSiteAdmin) {
      this.setState({loading: true})
      this.loadPortalsSubscription = ImplementationsGetObservable().subscribe(
        data => {
          this.setState({ portals: data.Records, loading: false })
        },
        error =>  this.handleAPIError(error, { loading: false, portals: [] })
      )
    }

    if (this.props.isPortalAdmin) {
      this.loadCompanyServicesSubscription = this.company$.pipe(
        switchMap(company => concat(
          of({ loadingServices: true }),
          AccountServicesGet(this.props.Portal?.ImplementationID || 0, company.AccountID).pipe(
            map(result => ({ loadingServices: false, result: result })),
            catchError(() => of({ loadingServices: false }))
          )
        ))
      ).subscribe(
        (o: { loadingServices: boolean, result?: AccountService[] }) => {
          this.setState({ loadingServices: o.loadingServices, accountServices: o.result || [] })
          this.populateServiceData()
        },
        (error) => this.handleAPIError(error, { loadingServices: false, accountServices: undefined })
      )
    }
    this.loadInitialData()
  }

  componentWillUnmount() {
    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.loadSubscription && this.loadSubscription.unsubscribe()
    this.loadPortalsSubscription && this.loadPortalsSubscription.unsubscribe()
    this.loadCompanyServicesSubscription && this.loadCompanyServicesSubscription.unsubscribe()
  }

  loadInitialData() {
    this.loadSubscription && this.loadSubscription.unsubscribe()
    this.setState({ loadingServices: true })

    if (this.props.isPortalAdmin) {
      this.loadSubscription = forkJoin({
        services: ServicesGet(),
        accountServices: AccountServicesGet(this.props.Portal?.ImplementationID || 0, this.state.selectedCompanyID)
      }).subscribe(
        ({ services, accountServices }) => {
          this.setState({ services: services, accountServices: accountServices, loadingServices: false })
          if (!this.state.selectedCompanyID) this.setState({ portalServices: accountServices })
          this.populateServiceData()
        },
        (error) => this.handleAPIError(error, { loadingServices: false })
      )
    }

    if (this.props.isSiteAdmin) {
      if (this.state.selectedPortal) {
        this.loadSubscription = forkJoin({
          services: ServicesGet(),
          accountServices: AccountServicesGet(this.state.selectedPortal.ImplementationID)
        }).subscribe(
          ({ services, accountServices }) => {
            this.setState({ services: services, accountServices: accountServices, loadingServices: false })
            this.populateServiceData()
          },
          (error) => this.handleAPIError(error, { loadingServices: false })
        )
      }
      else {
        this.loadSubscription = ServicesGet().subscribe(
          (data) => {
            this.setState({ services: data, loadingServices: false})
            this.populateServiceData()
          },
          (error) => this.handleAPIError(error, { loadingServices: false, services: [] })
        )
      }
    }

  }

  mapServiceToServiceData = (services: Service[]): ServiceData[] => services.map(service => {
    return { ServiceID: service.ServiceID, Active: service.ActiveWorkForce, IsDefault: false, ServiceName: service.ServiceName || "", ActivateAll: service.ActivateAll }
  })

  mapAccountServiceToServiceData = (accountServices: AccountService[]): ServiceData[] => {
    const { selectedCompanyID, portalServices } = this.state
    var mappedServices: ServiceData[] = []
    this.state.services.forEach(service => {
      const accountService = accountServices.find(s => s.ServiceID == service.ServiceID)
      const portalAccountService = portalServices.find(s => s.ServiceID == service.ServiceID)
      const disabled = selectedCompanyID ? !service.ActiveWorkForce || (portalServices.length > 0 && !portalAccountService?.Active) : !service.ActiveWorkForce
      if (accountService) {
        mappedServices.push({ AccountServiceID: accountService.AccountServiceID, ServiceID: accountService.ServiceID, ServiceName: service.ServiceName || "", Active: service.ActiveWorkForce && !disabled ? accountService.Active : false, IsDefault: accountService.IsDefault, Disabled: disabled, ActivateAll: false })
      }
      else {
        const active = (accountServices.length === 0 && !selectedCompanyID) || (!!selectedCompanyID && !!portalAccountService && portalAccountService.IsDefault)
        mappedServices.push({ ServiceID: service.ServiceID, ServiceName: service.ServiceName || "", Active: !disabled && active, IsDefault: false, Disabled: disabled, ActivateAll: false })
      }
    })
    return mappedServices
  }

  handleCancel = () => (this.props as RouteComponentProps).history?.push(Home)

  handleSave = () => {
    this.setState({ saving: true })

    var saveObj: SaveServices = {
      Services: [],
      AccountServices: []
    }

    if (this.props.isSiteAdmin) {
      if (this.state.selectedPortal) {
        saveObj.AccountServices = this.state.data.map(service => { return { AccountServiceID: service.AccountServiceID, ServiceID: service.ServiceID, Active: service.Active, IsDefault: service.IsDefault } })
        saveObj.ImplementationID = this.state.selectedPortal.ImplementationID
      }
      else {
        saveObj.Services = this.state.data.map(service => { return { ServiceID: service.ServiceID, ActiveWorkForce: service.Active, ActivateAll: service.ActivateAll } })
      }
    }

    if (this.props.isPortalAdmin) {
      saveObj.AccountServices = this.state.data.map(service => { return { AccountServiceID: service.AccountServiceID, ServiceID: service.ServiceID, Active: service.Active, IsDefault: service.IsDefault } })
      saveObj.ImplementationID = this.props.Portal?.ImplementationID
      saveObj.AccountID = this.state.selectedCompanyID
    }

    this.saveSubscription = AccountServicesSave(saveObj).subscribe(
      (data) => {
        this.setState({saving: false})
        toast.success(data)
        this.loadInitialData()
      },
      (error) => this.handleAPIError(error, { saving: false })
    )
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error(FormatError(error))
  }

  toggleActive = (index: number) => {
    const newData: ServiceData[] = [...this.state.data]
    newData[index].Active = !newData[index].Active
    newData[index].IsDefault = false
    newData[index].ActivateAll = false
    this.setState({ data: newData })
  }

  toggleDefault = (index: number) => {
    const newData: ServiceData[] = [...this.state.data]
    newData[index].IsDefault = !newData[index].IsDefault
    this.setState({ data: newData })
  }

  toggleActivate = (index: number) => {
    const newData: ServiceData[] = [...this.state.data]
    newData[index].ActivateAll = !newData[index].ActivateAll
    this.setState({ data: newData })
  }

  handlePortalChange = (selected: ImplementationData[]) => {
    const selectedPortal = selected[0]
    this.setState({ selectedPortal: selectedPortal })

    if (selectedPortal?.ImplementationID) {
      this.setState({ loadingServices: true })
      AccountServicesGet(selectedPortal?.ImplementationID).subscribe(
        (data) => {
          this.setState({ accountServices: data, loadingServices: false })
          this.populateServiceData()
        },
        (error) => this.handleAPIError(error, { loadingServices: false, accountServices: [] })
      )
    }
  }

  handleCompanyChange = (selected: AutoFilterCompany[]) => {
    const selectedCompany = selected[0]
    this.setState({ selectedCompany: selectedCompany })
    if (selectedCompany) {
      this.setState({ selectedCompanyID: selectedCompany.AccountID })
      this.company$.next(selectedCompany)
    } else {
      this.loadSubscription = AccountServicesGet(this.props.Portal?.ImplementationID || 0).subscribe(
        (data) => {
          this.setState({ accountServices: data, loadingServices: false, selectedCompanyID: undefined })
          this.populateServiceData()
        },
        (error) => this.handleAPIError(error, { loadingServices: false, accountServices: [], selectedCompanyID: undefined })
      )
    }
  }

  handleEmptyPortal = (company: string) => {
    if (company == "") {
      this.loadSubscription = ServicesGet().subscribe(
        (data) => {
          this.setState({ services: data, loadingServices: false, selectedPortal: undefined })
          this.populateServiceData()
        },
        (error) => this.handleAPIError(error, { loadingServices: false, services: [], selectedPortalID: undefined })
      )
    }
  }

  disableDefaultTooltip = () => {
    return <OverlayTrigger placement="right" delay={{ show: 100, hide: 400 }} overlay={
      <Popover id="defaultDisable">
        {DisabledDefaultOrderableService}
      </Popover>}>
      <FaInfoCircle className="info-icon" />
    </OverlayTrigger>
  }

  populateServiceData = () => {
    const { selectedPortal, selectedCompanyID, services, accountServices } = this.state
    const { isSiteAdmin, isPortalAdmin } = this.props
    var data: ServiceData[] = []
    if (isSiteAdmin) {
      data = selectedPortal ? this.mapAccountServiceToServiceData(accountServices.filter(s => !s.AccountID)) : this.mapServiceToServiceData(services)
    }
    if (isPortalAdmin) {
      data = selectedCompanyID ? this.mapAccountServiceToServiceData(accountServices.filter(s => s.AccountID == selectedCompanyID)) : this.mapAccountServiceToServiceData(accountServices.filter(s => !s.AccountID))
    }
    this.setState({ data: data })
  }

  DataRow = (data: ServiceData, index: number) => {
    const isSiteAdmin = !!this.props.isSiteAdmin
    const showDefault = (this.props.isPortalAdmin && !this.state.selectedCompanyID) || (this.props.isSiteAdmin && !!this.state.selectedPortal)
    const disable = this.props.isPortalAdmin && !!this.state.selectedCompanyID && !!this.state.portalServices.find(item => item.ServiceID === data.ServiceID)?.IsDefault
    return <tr key={data.ServiceID}>
      <td>{data.ServiceName}</td>
      {disable && <td className="pl-0">{this.disableDefaultTooltip()}</td>}
      {!disable && <td><Checkbox id={`toggleActive${data.ServiceID}`} checked={data.Active} disabled={data.Disabled || disable} onChange={() => this.toggleActive(index)} className="mr-auto" /></td>}
      {showDefault && <><td><Checkbox id={`toggleDefault${data.ServiceID}`} checked={data.IsDefault} disabled={data.Disabled || !data.Active} onChange={() => this.toggleDefault(index)} className="mr-auto" /></td></>}
      {!showDefault && isSiteAdmin && <><td><Checkbox id={`toggleActivate${data.ServiceID}`} checked={data.ActivateAll} disabled={data.Disabled || !data.Active} onChange={() => this.toggleActivate(index)} className="mr-auto" /></td></>}
    </tr>
  }

  render() {
    const { saving, loading, loadingServices, data, selectedCompanyID, selectedPortal, portals } = this.state
    const { isSiteAdmin, isPortalAdmin } = this.props
    const showDefault = (isPortalAdmin && !selectedCompanyID) || (isSiteAdmin && !!selectedPortal)

    return (
      <AppPageWrapper showNav>
        <Modal.Dialog size="lg">
          <Modal.Header>
            <Modal.Title className="text-uppercase">Orderable Services</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group as={Row}>
              {isSiteAdmin && <Col>
                <Typeahead id="portal"
                  ref={this.portalTypeahead}
                  labelKey="Name"
                  filterBy={(option, props) => props.selected.length > 0 || option.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                  isLoading={loading}
                  onChange={this.handlePortalChange}
                  onInputChange={text => this.handleEmptyPortal(text)}
                  disabled={loading || loadingServices}
                  options={portals || []}
                  selected={selectedPortal ? [selectedPortal || ""] : []}
                  placeholder="Select Portal"
                  emptyLabel="No Portals Found">
                  {!loading && <Button variant="toggle" onClick={() => { this.portalTypeahead.current?.toggleMenu() }}>
                    <FaAngleDown />
                  </Button>}
                </Typeahead>
              </Col>}
              {isPortalAdmin && <Col>
                <CompanyInput disabled={loading || loadingServices} onChange={items => this.handleCompanyChange(items)} placeholder="Select Company" />
              </Col>}
            </Form.Group>
            <Form.Group as={Row}>
              <Table responsive bordered className="dark text-center">
                {showDefault && <><thead><tr><th className="w-50">Service Name</th><th className="w-25">Orderable</th><th className="w-25">Default</th></tr></thead></>}
                {!showDefault && !isSiteAdmin && <><thead><tr><th className="w-50">Service Name</th><th className="w-50">Orderable</th></tr></thead></>}
                {!showDefault && isSiteAdmin && <><thead><tr><th className="w-50">Service Name</th><th className="w-25">Orderable</th><th className="w-25">Activate For All Portals</th></tr></thead></>}
                <tbody>{this.state.loadingServices ? <tr><td colSpan={2}><Spinner animation="border" size="sm" /></td></tr> :
                  data.map((service, index) => this.DataRow(service, index))}</tbody>
              </Table>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <ActionButton variant="primary" onClick={() => this.handleSave()} disabled={loading} spin={saving} disableOnSpin>Update</ActionButton>
            <Button variant="secondary" className="mr-2" onClick={() => this.handleCancel()}>Cancel</Button>
          </Modal.Footer>
        </Modal.Dialog>
      </AppPageWrapper>
    )
  }
}

function mapStateToProps(state: AppState): OrderableServicesModalAuthProps {
  return {
    isPortalAdmin: isPortalAdmin(state),
    isSiteAdmin: isSiteAdmin(state),
    UserName: getUserNameForSelectedCompany(state),
    Portal: GetSelectedPortal(state)
  }
}
const ConnectedOrderableServicesModal = connect(mapStateToProps, null)(OrderableServicesModal)
export default ConnectedOrderableServicesModal